/**
 * Inspector row (Phase 3 of pb-section-properties overhaul).
 *
 * A dense single-line summary of one section property:
 *
 *   [icon/override gutter] [label] [summary glyph] [chevron ›]
 *
 * Click → opens the row's drawer. Active state (drawer open) is set on the
 * row by drawer.js via `pb-spi-row-drawer-active`. Override rows show a
 * 4px accent dot in the gutter when the corresponding override is set;
 * content rows (Decorative SVG, Form) do not.
 *
 * Renders as a `<button>` so it's keyboard-operable by default. Returns a
 * detached element so callers can append it wherever they need.
 */

import { escapeHTML as escapeHtml } from '../../shared/html-escape.js';

const ROW_ICONS = {
  background:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="2.5"/><circle cx="9" cy="10" r="1.4"/><path d="m7 17 4.2-4.2a1.6 1.6 0 0 1 2.3 0L18 17"/></svg>',
  spacing:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 5h12"/><path d="M6 19h12"/><path d="M12 8v8"/><path d="m9 11 3-3 3 3"/><path d="m9 13 3 3 3-3"/></svg>',
  'custom-css':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m8 9-4 3 4 3"/><path d="m16 9 4 3-4 3"/><path d="m14 5-4 14"/></svg>',
  'decorative-svg':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5 13.8 9l5.7 1.8-5.7 1.8L12 18l-1.8-5.4-5.7-1.8L10.2 9 12 3.5z"/><path d="M18 16.5 19 19l2.5 1-2.5 1-1 2.5L17 21l-2.5-1L17 19l1-2.5z"/></svg>',
  form: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="4" width="14" height="16" rx="2"/><path d="M8.5 9h7"/><path d="M8.5 13h7"/><path d="M8.5 17h4"/></svg>',
  'design-colors':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.9 1.8-1.8 0-.6-.3-1-.3-1.6 0-.8.6-1.4 1.4-1.4H17a4 4 0 0 0 4-4c0-5-4-9-9-9z"/><circle cx="7.5" cy="11.5" r="1.2" fill="currentColor"/><circle cx="11" cy="7.5" r="1.2" fill="currentColor"/><circle cx="15" cy="8.5" r="1.2" fill="currentColor"/><circle cx="17" cy="12.5" r="1.2" fill="currentColor"/></svg>',
  'design-fonts':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4 19 6.5-13h1L18 19"/><path d="M7 14h8"/></svg>',
  'design-layout':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 9h16"/><path d="M10 9v11"/></svg>',
  'design-forms':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="5" width="16" height="3.5" rx="1"/><rect x="4" y="11" width="16" height="3.5" rx="1"/><rect x="4" y="17" width="9" height="2.5" rx="1" fill="currentColor"/></svg>'
};

/**
 * @param {{
 *   id: string,
 *   label: string,
 *   summaryHtml?: string,        // pre-escaped HTML for the right-side glyph
 *   summaryText?: string,        // fallback plain text if no summaryHtml
 *   hasOverride?: boolean,       // shows the override dot when true
 *   isContent?: boolean,         // dims gutter; never shows the dot
 *   ariaLabel?: string,          // overrides the auto-computed aria-label
 *   onOpen: (e: MouseEvent | KeyboardEvent) => void,
 * }} opts
 * @returns {HTMLButtonElement}
 */
export function renderInspectorRow(opts) {
  const {
    id,
    label,
    summaryHtml,
    summaryText,
    hasOverride = false,
    isContent = false,
    ariaLabel,
    onOpen
  } = opts || {};

  const row = document.createElement('button');
  row.type = 'button';
  row.className = [
    'pb-spi-row',
    isContent ? 'pb-spi-row-content' : 'pb-spi-row-override',
    hasOverride && !isContent ? 'pb-spi-row-has-override' : ''
  ]
    .filter(Boolean)
    .join(' ');
  row.dataset.spiRowId = id;
  row.setAttribute('aria-haspopup', 'dialog');
  row.setAttribute('aria-expanded', 'false');
  if (ariaLabel) row.setAttribute('aria-label', ariaLabel);

  const summaryInner = summaryHtml ? summaryHtml : summaryText ? escapeHtml(summaryText) : '';
  const icon =
    ROW_ICONS[id] ||
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l2.5 2"/></svg>';

  row.innerHTML = `
    <span class="pb-spi-row-gutter" aria-hidden="true"><span class="pb-spi-row-icon">${icon}</span>${hasOverride && !isContent ? '<span class="pb-spi-row-override-dot"></span>' : ''}</span>
    <span class="pb-spi-row-label">${escapeHtml(label || '')}</span>
    <span class="pb-spi-row-summary">${summaryInner}</span>
    <span class="pb-spi-row-chevron" aria-hidden="true">›</span>
  `;

  if (typeof onOpen === 'function') {
    row.addEventListener('click', onOpen);
  }
  return row;
}

/** Render the `─── Content ───` divider between override rows and content rows. */
export function renderInspectorContentDivider(label = 'Content') {
  const div = document.createElement('div');
  div.className = 'pb-spi-divider';
  div.setAttribute('role', 'separator');
  div.innerHTML = `
    <span class="pb-spi-divider-line" aria-hidden="true"></span>
    <span class="pb-spi-divider-label">${escapeHtml(label)}</span>
    <span class="pb-spi-divider-line" aria-hidden="true"></span>
  `;
  return div;
}
